import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { CheckCircle, Schedule, Person, Cancel } from '@mui/icons-material';
import { ApprovalRoleCardProps } from './types';

const ApprovalRoleCard = ({
  role,
  index,
  onApprove,
  onReject,
  canApprove,
}: ApprovalRoleCardProps) => {
  const getStatusColor = (approved: boolean) => {
    return approved ? 'success' : 'warning';
  };

  return (
    <Paper
      key={index}
      sx={{
        p: 2,
        mb: 2,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '40%' }}>
        <Box
          sx={{
            backgroundColor: role.approved
              ? 'rgba(46, 125, 50, 0.15)'
              : 'rgba(237, 108, 2, 0.15)',
            borderRadius: '50%',
            p: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {role.approved ? (
            <CheckCircle sx={{ fontSize: '1.5rem', color: 'success.main' }} />
          ) : (
            <Schedule sx={{ fontSize: '1.5rem', color: 'warning.main' }} />
          )}
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              lineHeight: 1,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
            }}
          >
            {role.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.75rem',
              lineHeight: 1,
            }}
          >
            Rol de Aprobación
          </Typography>
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Chip
        label={role.approved ? 'Aprobado' : 'Pendiente'}
        color={getStatusColor(role.approved) as any}
        size="small"
        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
            p: 0.75,
            borderRadius: '50%',
            mr: 1,
          }}
        >
          <Person sx={{ fontSize: '0.95rem', color: 'purple.main' }} />
        </Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1 }}
        >
          {role.approvedCount}/{role.approvedCountRequired}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.65rem', ml: 0.5 }}
        >
          Aprobaciones
        </Typography>
      </Box>

      {role.namesThatApprovedThisRole &&
        role.namesThatApprovedThisRole.length > 0 && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 120,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  p: 0.75,
                  borderRadius: '50%',
                  mr: 1,
                }}
              >
                <CheckCircle
                  sx={{ fontSize: '0.95rem', color: 'success.main' }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1 }}
              >
                {role.namesThatApprovedThisRole}
              </Typography>
            </Box>
          </>
        )}

      {canApprove && role.userCanApprove && !role.approved && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              ml: 'auto',
            }}
          >
            <Tooltip title="Aprobar solicitud" arrow>
              <IconButton
                onClick={onApprove}
                sx={{
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  color: 'success.main',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  },
                  width: 40,
                  height: 40,
                }}
              >
                <CheckCircle sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rechazar solicitud" arrow>
              <IconButton
                onClick={onReject}
                sx={{
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  },
                  width: 40,
                  height: 40,
                }}
              >
                <Cancel sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ApprovalRoleCard;
